import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Button from "../../UI/Button/Button";
import LoadingSpinner from "../../UI/LoadingSpinner/LoadingSpinner";
import { getUserDomainPlans } from "../../../services/domain-plan-service";
import { safeFormatDate } from "../../../utils/dateUtils.js";
import { useAuth } from "../../../context/authContext";
import styles from "./ManageDomains.module.css";

export default function ManageDomains() {
  const { t } = useTranslation("common");
  const { user } = useAuth();
  const navigate = useNavigate();
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedYears, setSelectedYears] = useState({});

  const getPriceForCurrency = (domain) => {
    const userCurrency = user?.currency || "RON";
    const price =
      userCurrency === "EUR"
        ? domain.pricePerYearEURO || 0
        : domain.pricePerYearRON || 0;

    return {
      amount: price,
      currency: userCurrency,
      symbol: getCurrencySymbol(userCurrency),
    };
  };

  const getCurrencySymbol = (currency) => {
    return currency === "EUR" ? "€" : "RON";
  };

  const normalizePrice = (price) => {
    if (typeof price !== "number") return 0;
    return price;
  };

  const fetchDomains = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await getUserDomainPlans();

      if (response.success && Array.isArray(response.data)) {
        setDomains(response.data);

        const initialYears = {};
        response.data.forEach((domain) => {
          initialYears[domain._id] = 1;
        });
        setSelectedYears(initialYears);
      } else {
        setDomains([]);
      }
    } catch (err) {
      console.error("Error fetching domains:", err);
      setError(
        err.message ||
          t("billingPage.manageDomains.errorLoading", { error: err.message }),
      );
      setDomains([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchDomains();
  }, [fetchDomains]);

  const formatDate = (dateString) => {
    if (!dateString || isNaN(new Date(dateString).getTime())) {
      const currentDate = new Date();
      return currentDate.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    }

    return safeFormatDate(dateString, {}, "Invalid Date");
  };

  const getStatusBadge = (renewalDate) => {
    const now = new Date();
    const expDate = new Date(renewalDate);
    const daysUntilExpiry = Math.floor((expDate - now) / (1000 * 60 * 60 * 24));

    let status = "active";
    if (daysUntilExpiry < 0) {
      status = "expired";
    } else if (daysUntilExpiry <= 30) {
      status = "expiring soon";
    }

    const statusClasses = {
      active: styles.statusActive,
      "expiring soon": styles.statusExpiringSoon,
      expired: styles.statusExpired,
    };
    return `${styles.statusBadge} ${
      statusClasses[status] || styles.statusDefault
    }`;
  };

  const getStatusText = (renewalDate) => {
    const now = new Date();
    const expDate = new Date(renewalDate);
    const daysUntilExpiry = Math.floor((expDate - now) / (1000 * 60 * 60 * 24));

    if (daysUntilExpiry < 0) {
      return t("billingPage.manageDomains.status.expired");
    } else if (daysUntilExpiry <= 30) {
      return t("billingPage.manageDomains.status.expiringSoon");
    }
    return t("billingPage.manageDomains.status.active");
  };

  const handleExtendDomain = async (domainId, years) => {
    if (!years || years === "") {
      return;
    }

    setSelectedYears((prev) => ({
      ...prev,
      [domainId]: Number.parseInt(years),
    }));

    const domain = domains.find((d) => d._id === domainId);
    if (!domain) {
      return;
    }

    const yearsNum = Number.parseInt(years);
    const priceInfo = getPriceForCurrency(domain);
    const normalizedPricePerYear = normalizePrice(priceInfo.amount);
    const totalAmount = yearsNum * normalizedPricePerYear;

    const newPlanId = generateDomainPlanId();

    const params = new URLSearchParams({
      type: "domain_extension",
      domain: domain.product,
      description: `Domain Extension (${yearsNum} Year${
        yearsNum > 1 ? "s" : ""
      })`,
      amount: totalAmount.toFixed(2),
      currency: priceInfo.currency,
      years: yearsNum.toString(),
      requiresBillingInfo: "false",
      isSubscription: "false",
      domainId: domainId,
      pricePerYear: normalizedPricePerYear.toString(),
      planId: newPlanId,
      currentExpirationDate: domain.renewalDate,
    });

    navigate(`/checkout?${params.toString()}`);
  };

  const generateDomainPlanId = (date = new Date()) => {
    const yy = String(date.getFullYear()).slice(-2);
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const randomNum = Math.floor(Math.random() * 9000) + 1000;
    return `PLDMN${yy}${dd}${mm}${randomNum}`;
  };

  const handleRetry = () => {
    fetchDomains();
  };

  const getButtonText = (renewalDate) => {
    const now = new Date();
    const expDate = new Date(renewalDate);
    const daysUntilExpiry = Math.ceil((expDate - now) / (1000 * 60 * 60 * 24));

    if (daysUntilExpiry < 0) {
      return t("billingPage.manageDomains.actions.buy");
    }
    return t("billingPage.manageDomains.actions.extend");
  };

  if (loading) {
    return (
      <div className={styles.tabContent}>
        <div className={styles.loadingContainer}>
          <LoadingSpinner />
          <p>{t("billingPage.manageDomains.loadingDomains")}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.tabContent}>
        <div className={styles.errorContainer}>
          <p className={styles.errorMessage}>
            {t("billingPage.manageDomains.errorLoading", { error })}
          </p>
          <Button onClick={handleRetry}>
            {t("billingPage.manageDomains.retry")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={styles.tabContent}>
        <div className={styles.sectionHeader}>
          <div>
            <h2 className={styles.sectionTitle}>
              {t("billingPage.manageDomains.title")}
            </h2>
            <p className={styles.sectionDescription}>
              {t("billingPage.manageDomains.description")}
            </p>
          </div>
          <Button onClick={fetchDomains} style={{ padding: "12px 24px" }}>
            {t("billingPage.manageDomains.refreshDomains")}
          </Button>
        </div>

        {domains.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyStateIcon} aria-hidden="true">
              <svg width="56" height="56" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10Z"
                  stroke="currentColor"
                  strokeWidth="2"
                />
                <path
                  d="M2 12h20"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <path
                  d="M12 2a14 14 0 0 1 0 20"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <path
                  d="M12 2a14 14 0 0 0 0 20"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </div>
            <p className={styles.emptyStateMessage}>
              {t("billingPage.manageDomains.noDomains")}
            </p>
          </div>
        ) : (
          <>
            <div className={styles.tableContainer}>
              <table className={styles.billingTable}>
                <thead>
                  <tr>
                    <th>
                      {t("billingPage.manageDomains.tableHeaders.projectId")}
                    </th>
                    <th>
                      {t("billingPage.manageDomains.tableHeaders.product")}
                    </th>
                    <th>
                      {t("billingPage.manageDomains.tableHeaders.renewalDate")}
                    </th>
                    <th>
                      {t("billingPage.manageDomains.tableHeaders.status")}
                    </th>
                    <th>
                      {t("billingPage.manageDomains.tableHeaders.pricePerYear")}
                    </th>
                    <th>
                      {t("billingPage.manageDomains.tableHeaders.extendTime")}
                    </th>
                    <th>
                      {t("billingPage.manageDomains.tableHeaders.actions")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {domains.map((domain, index) => {
                    const years = selectedYears[domain._id] || 1;
                    const priceInfo = getPriceForCurrency(domain);
                    const normalizedPrice = normalizePrice(priceInfo.amount);
                    const totalPrice = normalizedPrice * years;

                    return (
                      <tr
                        key={domain._id}
                        className={
                          index % 2 === 0 ? styles.rowEven : styles.rowOdd
                        }
                      >
                        <td className={styles.invoiceId}>{domain.projectId}</td>
                        <td className={styles.domainName}>
                          <span className={styles.domainIcon}>🌐</span>
                          {domain.product}
                        </td>
                        <td>{formatDate(domain.renewalDate)}</td>
                        <td>
                          <span className={getStatusBadge(domain.renewalDate)}>
                            {getStatusText(domain.renewalDate)}
                          </span>
                        </td>
                        <td className={styles.amount}>
                          <div className={styles.priceDisplay}>
                            <div className={styles.totalPrice}>
                              {priceInfo.symbol} {totalPrice.toFixed(2)}
                            </div>
                            <div className={styles.priceBreakdown}>
                              {years > 1
                                ? `${years} ${t(
                                    "billingPage.manageDomains.priceFormat.multiplier",
                                  )} ${
                                    priceInfo.symbol
                                  } ${normalizedPrice.toFixed(2)}`
                                : `${
                                    priceInfo.symbol
                                  } ${normalizedPrice.toFixed(2)}${t(
                                    "billingPage.manageDomains.priceFormat.perYear",
                                  )}`}
                            </div>
                          </div>
                        </td>
                        <td>
                          <select
                            className={styles.extendDropdown}
                            value={selectedYears[domain._id] || 1}
                            onChange={(e) => {
                              const newYears = Number.parseInt(e.target.value);
                              setSelectedYears((prev) => ({
                                ...prev,
                                [domain._id]: newYears,
                              }));
                            }}
                          >
                            <option value="1">
                              {t(
                                "billingPage.manageDomains.extendOptions.1year",
                              )}
                            </option>
                            <option value="2">
                              {t(
                                "billingPage.manageDomains.extendOptions.2years",
                              )}
                            </option>
                            <option value="3">
                              {t(
                                "billingPage.manageDomains.extendOptions.3years",
                              )}
                            </option>
                            <option value="4">
                              {t(
                                "billingPage.manageDomains.extendOptions.4years",
                              )}
                            </option>
                            <option value="5">
                              {t(
                                "billingPage.manageDomains.extendOptions.5years",
                              )}
                            </option>
                          </select>
                        </td>
                        <td>
                          <Button
                            onClick={() =>
                              handleExtendDomain(
                                domain._id,
                                selectedYears[domain._id] || 1,
                              )
                            }
                            style={{ padding: "8px 16px", fontSize: "14px" }}
                          >
                            {getButtonText(domain.renewalDate)}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className={styles.cardsContainer}>
              {domains.map((domain) => {
                const years = selectedYears[domain._id] || 1;
                const priceInfo = getPriceForCurrency(domain);
                const normalizedPrice = normalizePrice(priceInfo.amount);
                const totalPrice = normalizedPrice * years;

                return (
                  <div key={domain._id} className={styles.domainCard}>
                    <div className={styles.cardHeader}>
                      <div className={styles.domainInfo}>
                        <div className={styles.domainId}>
                          {domain.projectId}
                        </div>
                        <div className={styles.domainName}>
                          <span className={styles.domainIcon}>🌐</span>
                          {domain.product}
                        </div>
                      </div>
                      <span className={getStatusBadge(domain.renewalDate)}>
                        {getStatusText(domain.renewalDate)}
                      </span>
                    </div>

                    <div className={styles.cardBody}>
                      <div className={styles.cardRow}>
                        <span className={styles.cardLabel}>
                          {t(
                            "billingPage.manageDomains.cardLabels.renewalDate",
                          )}
                        </span>
                        <span className={styles.cardValue}>
                          {formatDate(domain.renewalDate)}
                        </span>
                      </div>

                      <div className={styles.cardRow}>
                        <span className={styles.cardLabel}>
                          {t(
                            "billingPage.manageDomains.cardLabels.pricePerYear",
                          )}
                        </span>
                        <span className={styles.cardValue}>
                          <div className={styles.priceDisplay}>
                            <div className={styles.totalPrice}>
                              {priceInfo.symbol} {totalPrice.toFixed(2)}
                            </div>
                            <div className={styles.priceBreakdown}>
                              {years > 1
                                ? `${years} ${t(
                                    "billingPage.manageDomains.priceFormat.multiplier",
                                  )} ${
                                    priceInfo.symbol
                                  } ${normalizedPrice.toFixed(2)}`
                                : `${
                                    priceInfo.symbol
                                  } ${normalizedPrice.toFixed(2)}${t(
                                    "billingPage.manageDomains.priceFormat.perYear",
                                  )}`}
                            </div>
                          </div>
                        </span>
                      </div>

                      <div className={styles.cardRow}>
                        <span className={styles.cardLabel}>
                          {t("billingPage.manageDomains.cardLabels.extendTime")}
                        </span>
                        <span className={styles.cardValue}>
                          <select
                            className={styles.extendDropdown}
                            value={selectedYears[domain._id] || 1}
                            onChange={(e) => {
                              const newYears = Number.parseInt(e.target.value);
                              setSelectedYears((prev) => ({
                                ...prev,
                                [domain._id]: newYears,
                              }));
                            }}
                          >
                            <option value="1">
                              {t(
                                "billingPage.manageDomains.extendOptions.1year",
                              )}
                            </option>
                            <option value="2">
                              {t(
                                "billingPage.manageDomains.extendOptions.2years",
                              )}
                            </option>
                            <option value="3">
                              {t(
                                "billingPage.manageDomains.extendOptions.3years",
                              )}
                            </option>
                            <option value="4">
                              {t(
                                "billingPage.manageDomains.extendOptions.4years",
                              )}
                            </option>
                            <option value="5">
                              {t(
                                "billingPage.manageDomains.extendOptions.5years",
                              )}
                            </option>
                          </select>
                        </span>
                      </div>
                    </div>

                    <div className={styles.cardActions}>
                      <Button
                        onClick={() =>
                          handleExtendDomain(
                            domain._id,
                            selectedYears[domain._id] || 1,
                          )
                        }
                        style={{
                          padding: "12px 16px",
                          fontSize: "14px",
                          width: "100%",
                        }}
                      >
                        {getButtonText(domain.renewalDate)}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </>
  );
}
